export const STATIC_PART_AREA_SYMBOLS = [
  'circle',
  'slash',
  'plus',
  'minus',
  'triangle',
  'square'
] as const

export type StaticPartAreaSymbol = (typeof STATIC_PART_AREA_SYMBOLS)[number]

/**
 * Returns the symbol assigned to an area by its index. Once the ordered symbol
 * set is exhausted, areas are left without a symbol (undefined).
 */
export const getAreaSymbol = (areaIndex: number): StaticPartAreaSymbol | undefined =>
  STATIC_PART_AREA_SYMBOLS[areaIndex]

interface StaticPartSymbolShapeProps {
  symbol: StaticPartAreaSymbol
  cx: number
  cy: number
  size: number
  strokeWidth: number
  color?: string
}

/**
 * Renders a single symbol as SVG primitives centered at (cx, cy). Reused by the
 * printout drawing overlay and the "Visual Symbol" table cell so both stay in sync.
 */
export const StaticPartSymbolShape = ({
  symbol,
  cx,
  cy,
  size,
  strokeWidth,
  color = 'currentColor'
}: StaticPartSymbolShapeProps) => {
  const r = size / 2
  const stroke = {
    stroke: color,
    strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    fill: 'none'
  }

  switch (symbol) {
    case 'circle':
      return <circle cx={cx} cy={cy} r={r} {...stroke} />
    case 'slash':
      return <line x1={cx - r} y1={cy + r} x2={cx + r} y2={cy - r} {...stroke} />
    case 'plus':
      return (
        <>
          <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} {...stroke} />
          <line x1={cx} y1={cy - r} x2={cx} y2={cy + r} {...stroke} />
        </>
      )
    case 'minus':
      return <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} {...stroke} />
    case 'triangle':
      return (
        <polygon points={`${cx},${cy - r} ${cx + r},${cy + r} ${cx - r},${cy + r}`} {...stroke} />
      )
    case 'square':
      return <rect x={cx - r} y={cy - r} width={size} height={size} {...stroke} />
  }
}

interface StaticPartSymbolGlyphProps {
  symbol: StaticPartAreaSymbol
  repeat?: number
}

/**
 * Small inline representation of a symbol for use inside the report table
 * (e.g. "///", "OOO"). Repeats the glyph to mirror the design.
 */
export const StaticPartSymbolGlyph = ({symbol, repeat = 3}: StaticPartSymbolGlyphProps) => {
  const box = 14

  return (
    <div className="flex items-center gap-1 text-neutral-900" data-cy="static_part_symbol_glyph">
      {Array.from({length: repeat}).map((_, index) => (
        <svg key={index} width={box} height={box} viewBox={`0 0 ${box} ${box}`} aria-hidden>
          <StaticPartSymbolShape
            symbol={symbol}
            cx={box / 2}
            cy={box / 2}
            size={box * 0.7}
            strokeWidth={1.3}
          />
        </svg>
      ))}
    </div>
  )
}
